import { Button, Input } from '@besiktning/ui'
import { Plus, Trash2 } from 'lucide-react'
import {
  formatCurrency,
  computeTaxBreakdownFromLines,
  TaxBreakdownSummary,
} from '@besiktning/ui'
import { t } from '../i18n'

export interface LineItemForm {
  id?: string;
  description: string;
  quantity: number;
  unit_price: number;
  unit_type: string;
}

export const emptyLineItem = (): LineItemForm => ({
  description: '',
  quantity: 1,
  unit_price: 0,
  unit_type: 'st',
})

export const lineItemsTotal = (items: LineItemForm[]): number =>
  items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0)

interface Props {
  items: LineItemForm[];
  onChange: (items: LineItemForm[]) => void;
  /** Repeat the add button under the list — for long lists inside a modal. */
  addRowAtBottom?: boolean;
  /** Drop the heading when the surrounding card already names the section. */
  hideTitle?: boolean;
}

/** The rows a job is billed on. Used for both quotes and booked jobs. */
export default function WorkOrderEditor({ items, onChange, addRowAtBottom = false, hideTitle = false }: Props) {
  const addRow = () => onChange([...items, emptyLineItem()])
  const removeRow = (index: number) => onChange(items.filter((_, i) => i !== index))
  const updateRow = (index: number, field: keyof LineItemForm, value: string | number) =>
    onChange(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)))

  const total = lineItemsTotal(items)
  const breakdown = items.length > 0 ? computeTaxBreakdownFromLines(items) : null

  return (
    <div className="space-y-3">
      <div className={`flex items-center ${hideTitle ? 'justify-end' : 'justify-between'}`}>
        {!hideTitle && (
          <h3 className="text-sm font-medium text-gray-700">{t('requestDetail.workOrder')}</h3>
        )}
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus className="h-4 w-4 mr-1" />
          {t('requestDetail.addRow')}
        </Button>
      </div>

      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder={t('requestDetail.descriptionPlaceholder')}
                  value={item.description}
                  onChange={(e) => updateRow(index, 'description', e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRow(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder={t('requestDetail.quantityPlaceholder')}
                  value={item.quantity || ''}
                  onChange={(e) => updateRow(index, 'quantity', parseInt(e.target.value) || 0)}
                  className="w-20"
                />
                <select
                  value={item.unit_type}
                  onChange={(e) => updateRow(index, 'unit_type', e.target.value)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm"
                >
                  <option value="st">st</option>
                  <option value="tim">tim</option>
                  <option value="h">h</option>
                </select>
                <Input
                  type="number"
                  placeholder={t('requestDetail.unitPricePlaceholder')}
                  value={item.unit_price || ''}
                  onChange={(e) => updateRow(index, 'unit_price', parseInt(e.target.value) || 0)}
                  className="w-36"
                />
                <div className="flex items-center text-sm text-gray-600 w-24 justify-end">
                  {formatCurrency(item.quantity * item.unit_price)}
                </div>
              </div>
            </div>
          ))}

          {/* Repeated at the bottom: with several rows the header button
              scrolls out of the modal and looks like the list is fixed. */}
          {addRowAtBottom && (
            <Button type="button" variant="outline" size="sm" onClick={addRow} className="w-full">
              <Plus className="h-4 w-4 mr-1" />
              {t('requestDetail.addRow')}
            </Button>
          )}

          {breakdown && total > 0 ? (
            <TaxBreakdownSummary breakdown={breakdown} className="pt-3 border-t border-gray-200" />
          ) : null}
        </div>
      ) : (
        <div className="text-center py-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500">{t('requestDetail.noWorkOrders')}</p>
          <p className="text-xs text-gray-400">{t('requestDetail.clickAddRowHint')}</p>
        </div>
      )}
    </div>
  )
}
